import { IMAGE_LOADER, ImageLoaderConfig, LocationStrategy, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  APP_ID,
  ApplicationConfig,
  CSP_NONCE,
  EnvironmentInjector,
  inject,
  Injectable,
  InjectionToken,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { LuxonDateAdapter, MAT_LUXON_DATE_FORMATS } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, UrlSerializer } from '@angular/router';
import { persistState } from '@datorama/akita';
import { DeviceDetectorService } from 'ngx-device-detector';
import { v4 as uuid } from 'uuid';
import { environment } from '../environments/environment';
import { LowerCaseUrlSerializer, routes } from './app.routes';
import { AccessibilityService, accessibilityServiceFactory } from './services/accessibility/accessibility.service';
import { mParticleServiceFactory } from './services/analytics/mparticle.service';
import { salesIQServiceFactory } from './services/analytics/salesiq.service';
import { authInterceptor, AuthService, authServiceFactory, errorInterceptor } from './services/auth';
import { BasketState, initialBasketState } from './services/basket/basket.store';
import { CategoryService, categoryServiceFactory } from './services/category/category.service';
import { CheckoutService, checkoutServiceFactory } from './services/checkout/checkout.service';
import { CustomerDetailService, customerDetailServiceFactory } from './services/checkout/customer-details.service';
import { CustomerDetailState } from './services/checkout/customer-details.store';
import { ContentService } from './services/content/content.service';
import { CookiePolicyService, cookieServiceFactory } from './services/cookie/cookie.service';
import { UniversalDeviceDetectorService } from './services/device-detector/universal-device-detector.service';
import { KioskService, kioskServiceFactory } from './services/kiosk/kiosk.service';
import { PreviewPreserveQueryLocationStrategy } from './services/preview/preview.strategy';
import { productCategoryTagServiceFactory, ProductTagService } from './services/product/product-tag.service';
import { PermanentRedirectService } from './services/redirect';
import { initialShippingState, ShippingService, shippingServiceFactory, ShippingState } from './services/shipping';
import { ProductModulePipe } from './shared/pipes';

import localeDA from '@angular/common/locales/da';
import { SecurityService, securityServiceFactory } from './services/security/security.service';
registerLocaleData(localeDA, 'da');

// Create custom Injection Token
// https://medium.com/@gmurop/managing-dependencies-among-app-initializers-in-angular-652be4afce6f
const ConfigDeps = new InjectionToken<(() => Promise<any>)[]>('configDeps');

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}

const applicationState = persistState({
  include: ['basket', 'customer-details', 'shipping'],
  preStorageUpdate: (storeName: string, state) => {
    if (storeName === 'basket') {
      const basketState: BasketState = state;
      const { products, lineId } = basketState;

      let openShippingDialog = true;
      if (Object.values(products).length > 0) {
        openShippingDialog = false;
      }

      return {
        lineId,
        products,
        ui: {
          ...initialBasketState.ui,
          id: uuid(),
          openShippingDialog,
        },
      };
    }

    if (storeName === 'shipping') {
      const shippingState: ShippingState = state;

      const { shipping, eventStore } = shippingState;

      return {
        ...initialShippingState,
        shipping,
        eventStore,
      };
    }

    if (storeName === 'customer-details') {
      const customerDetailsState: CustomerDetailState = JSON.parse(JSON.stringify(state));
      customerDetailsState.customerDetails.payment.note = '';

      return {
        ...customerDetailsState,
      };
    }

    return state;
  },
});

@Injectable()
class AppDateAdapter extends LuxonDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: APP_ID, useValue: 'nxt-ecom-frontend' },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({ includeHeaders: ['host'], includeRequestsWithAuthHeaders: false, includePostRequests: false }),
    ),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideAnimationsAsync(),
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => {
        if (!config?.src.startsWith('/')) return config?.src;
        const url = `${environment.cmsOrigin}${config.src}?`;

        const queryParams = [];
        if (config.width) queryParams.push(`width=${config.width}`);
        if (config.loaderParams?.['height']) queryParams.push(`height=${config.loaderParams['height']}`);
        if (config.loaderParams?.['autorotate']) queryParams.push(`autorotate=${config.loaderParams['autorotate']}`);
        if (config.loaderParams?.['quality']) queryParams.push(`quality=${config.loaderParams['quality']}`);

        return url + queryParams.join('&');
      },
    },

    // Shared providers
    provideAppInitializer(() => {
      const contentService = inject(ContentService);
      const envInjector = inject(EnvironmentInjector);
      const redirectService = inject(PermanentRedirectService);
      const configDeps = inject(ConfigDeps);

      redirectService.load();

      const [authFactory, ...rest] = configDeps;
      return contentService
        .load()
        .then(async () => {
          await authFactory();
        })
        .then(async () => {
          await Promise.all(rest.map((dep) => dep()));
        })
        .then(() => {
          mParticleServiceFactory(contentService, envInjector);
          salesIQServiceFactory(contentService, envInjector);
        });
    }),
    {
      provide: ConfigDeps,
      useFactory: (
        authService: AuthService,
        kioskService: KioskService,
        customerDetailService: CustomerDetailService,
        shippingService: ShippingService,
        categoryService: CategoryService,
        checkoutService: CheckoutService,
        productTagService: ProductTagService,
        accessibilityService: AccessibilityService,
        cookiePolicyService: CookiePolicyService,
        securityService: SecurityService,
      ) => {
        return [
          authServiceFactory(authService),
          kioskServiceFactory(kioskService),
          customerDetailServiceFactory(customerDetailService),
          shippingServiceFactory(shippingService),
          categoryServiceFactory(categoryService),
          checkoutServiceFactory(checkoutService),
          productCategoryTagServiceFactory(productTagService),
          accessibilityServiceFactory(accessibilityService),
          cookieServiceFactory(cookiePolicyService),
          securityServiceFactory(securityService),
        ];
      },
      deps: [
        AuthService,
        KioskService,
        CustomerDetailService,
        ShippingService,
        CategoryService,
        CheckoutService,
        ProductTagService,
        AccessibilityService,
        CookiePolicyService,
        SecurityService,
      ],
    },
    { provide: LOCALE_ID, useValue: environment.i18n.language },
    { provide: MAT_DATE_LOCALE, useValue: environment.i18n.language },
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    { provide: DateAdapter, useClass: AppDateAdapter, deps: [MAT_DATE_LOCALE] },
    {
      provide: UrlSerializer,
      useClass: LowerCaseUrlSerializer,
    },
    {
      provide: DeviceDetectorService,
      useClass: UniversalDeviceDetectorService,
    },
    { provide: LocationStrategy, useClass: PreviewPreserveQueryLocationStrategy },

    // Pipes
    ProductModulePipe,

    // Static providers
    { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] },
    { provide: 'persistStorage', useValue: applicationState },
  ],
};
